import { Injectable } from '@angular/core';
import { AnalyticsAdapter } from '../core/analytics-adapter.interface';
import { PostHogProvider } from '../providers/posthog/posthog.provider';
import { MoEngageProvider } from '../providers/moengage/moengage.provider';
import { environment } from '../../../environments/environment';

export interface ProviderFactory {
  name: string;
  create: () => AnalyticsAdapter;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsFactoryService {
  private readonly providerFactories: Map<string, ProviderFactory> = new Map();

  constructor() {
    this.registerDefaultProviders();
  }

  private registerDefaultProviders(): void {
    // Register PostHog provider with its own configuration
    this.registerProvider('posthog', {
      name: 'PostHog',
      create: () => {
        const config = {
          enabled: true,
          key: (environment as any).POSTHOG_KEY || '',
          host: (environment as any).POSTHOG_HOST || '',
          debugLogs: false,
          timeout: 5000,
          retryAttempts: 2,
          retryDelay: 1000,
          disableCompression: true,
          capturePageview: true,
          autocapture: true,
          personProfiles: 'identified_only' as const,
          persistence: 'localStorage' as const,
          captureMetrics: false
        };
        return new PostHogProvider(config);
      }
    });

    // Register MoEngage provider with its own configuration
    this.registerProvider('moengage', {
      name: 'MoEngage',
      create: () => {
        const config = {
          enabled: true,
          appId: (environment as any).MOENGAGE_APP_ID || '',
          cluster: (environment as any).MOENGAGE_CLUSTER || '',
          debugLogs: true,
          timeout: 5000,
          retryAttempts: 3,
          retryDelay: 2000,
          enableSPA: true,
          swPath: '/sr_login/serviceworker.js',
          trackLocation: false,
          trackDeviceInfo: true
        };
        return new MoEngageProvider(config);
      }
    });
  }

  /**
   * Register a new analytics provider factory
   * @param name - Provider name
   * @param factory - Provider factory
   */
  registerProvider(name: string, factory: ProviderFactory): void {
    this.providerFactories.set(name.toLowerCase(), factory);
    console.log(`[AnalyticsFactory] Registered provider: ${name}`);
  }

  /**
   * Create a provider instance
   * @param name - Provider name
   * @returns Provider instance
   */
  createProvider(name: string): AnalyticsAdapter | null {
    const factory = this.providerFactories.get(name.toLowerCase());
    
    if (!factory) {
      console.error(`[AnalyticsFactory] Provider factory not found: ${name}`);
      return null;
    }

    try {
      const provider = factory.create();
      console.log(`[AnalyticsFactory] Created provider: ${name}`);
      return provider;
    } catch (error) {
      console.error(`[AnalyticsFactory] Error creating provider ${name}:`, error);
      return null;
    }
  }

  /**
   * Get list of available provider names
   * @returns Array of provider names
   */
  getAvailableProviders(): string[] {
    return Array.from(this.providerFactories.keys());
  }

  /**
   * Check if a provider is available
   * @param name - Provider name
   * @returns True if provider is available
   */
  isProviderAvailable(name: string): boolean {
    return this.providerFactories.has(name.toLowerCase());
  }

  /**
   * Get provider factory information
   * @param name - Provider name
   * @returns Provider factory info or null
   */
  getProviderFactory(name: string): ProviderFactory | null {
    return this.providerFactories.get(name.toLowerCase()) || null;
  }
} 